import * as React from 'react';
import { createRoot, Root } from 'react-dom/client';
import AdaptableReact, {
  AdaptableApi,
  AdaptableButton,
  AdaptableOptions,
  AlertButton,
  AlertFormContext,
  BaseContext,
  CellChangedInfo,
  ColumnFilter,
  CustomToolbarButtonContext,
  CustomToolPanelButtonContext,
  DataSetSelectedInfo,
  GridDataChangedInfo,
  PredicateDefHandlerContext,
} from '@adaptabletools/adaptable-react-aggrid';
import { AgGridReact } from '@ag-grid-community/react';

import { GridOptions, Module } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { SideBarModule } from '@ag-grid-enterprise/side-bar';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection';
import { RichSelectModule } from '@ag-grid-enterprise/rich-select';
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export';
import { GridChartsModule } from '@ag-grid-enterprise/charts';
import { SparklinesModule } from '@ag-grid-enterprise/sparklines';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { ClipboardModule } from '@ag-grid-enterprise/clipboard';

import { columnDefs } from './colDefs';
import { useData } from './useData';
import { getMyUser, MY_USER_NAME } from './api/users';
import { AboutPanel } from './AboutPanel';
import { AdaptableCellChangedAlert } from '@adaptabletools/adaptable/src/PredefinedConfig/Common/AdaptableAlert';
import { EditLookUpContext } from '@adaptabletools/adaptable/src/AdaptableOptions/UserInterfaceOptions';

// import adaptable css and themes
import '@adaptabletools/adaptable-react-aggrid/base.css';
import '@adaptabletools/adaptable-react-aggrid/themes/light.css';
import '@adaptabletools/adaptable-react-aggrid/themes/dark.css';

// import aggrid themes (using new Alpine theme)
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-alpine.css';

const renderWeakMap: WeakMap<HTMLElement, Root> = new WeakMap();

export const RECOMMENDED_MODULES: Module[] = [
  ClientSideRowModelModule,
  SideBarModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  StatusBarModule,
  MenuModule,
  RangeSelectionModule,
  RichSelectModule,
  ExcelExportModule,
  GridChartsModule,
  SparklinesModule,
  RowGroupingModule,
  ClipboardModule,
];

const modules = RECOMMENDED_MODULES;

let adaptableApiGlobal: AdaptableApi | null = null;

const getAllUsers = () => {
  const allUsers: any[] = [getMyUser()];
  const idMap: { [key: string]: boolean } = {};
  adaptableApiGlobal?.gridApi.getGridData()?.forEach((data) => {
    const login = data?.['user:login'];
    if (login && !idMap[login]) {
      const user = {
        login,
        html_url: data?.['user:html_url'],
        avatar_url: data?.['user:avatar_url'],
      };
      idMap[login] = true;
      allUsers.push(user);
    }
  });
  return allUsers;
};

const getAllMyOpenIssues = (adaptableApi: AdaptableApi) =>
  adaptableApi.gridApi
    .getGridData()
    .filter(
      (data) =>
        data?.['assignee:login'] === MY_USER_NAME && data?.state !== 'closed'
    );

const assignToMe = (api: AdaptableApi, row: any) => {
  const myUser = getMyUser();
  row = {
    ...row,
    'assignee:id': myUser.id,
    'assignee:html_url': myUser['html_url'],
    'assignee:login': myUser.login,
    'assignee:avatar_url': myUser['avatar_url'],
  };

  api?.gridApi.updateGridData([row]);
};

const isMyIssue = (data: any) => {
  return [data['assignee:login'], data['user:login']].includes(MY_USER_NAME);
};

const getMyIssuesFilter = (api: AdaptableApi): ColumnFilter | undefined => {
  return api.layoutApi.getCurrentLayout().ColumnFilters?.find((filter) => {
    return (
      filter.ColumnId === 'assignee:login' &&
      filter.Predicate.PredicateId === 'my-assignments'
    );
  });
};

const isMyIssuesFilterApplied = (api: AdaptableApi): boolean => {
  return Boolean(getMyIssuesFilter(api));
};

const getIssuesFilterLabel = (api: AdaptableApi) => {
  return isMyIssuesFilterApplied(api) ? 'Show All Issues' : 'Show My Issues';
};

const filterMyIssues = (api: AdaptableApi) => {
  if (isMyIssuesFilterApplied(api)) {
    const myIssuesFilter = getMyIssuesFilter(api);
    myIssuesFilter && api.columnFilterApi.clearColumnFilter(myIssuesFilter);
  } else {
    api.columnFilterApi.setColumnFilters([
      {
        ColumnId: 'assignee:login',
        Predicate: {
          PredicateId: 'my-assignments',
        },
      },
    ]);
  }
};

function getMyAllIssuesButton<
  CONTEXT extends BaseContext,
>(): AdaptableButton<CONTEXT> {
  return {
    label: (label, context) => {
      return getIssuesFilterLabel(context.adaptableApi);
    },
    buttonStyle: {
      variant: 'outlined',
      tone: 'info',
    },
    onClick: (button: AdaptableButton<CONTEXT>, context: CONTEXT) => {
      filterMyIssues(context.adaptableApi);
    },
  };
}

function getCloseAllMyIssuesButton<
  CONTEXT extends BaseContext,
>(): AdaptableButton<CONTEXT> {
  return {
    label: 'Close My Issues',
    buttonStyle: {
      variant: 'raised',
      tone: 'success',
    },
    onClick: (button: AdaptableButton<CONTEXT>, context: CONTEXT) => {
      context.adaptableApi.gridApi.getGridData().forEach((data) => {
        if (data['assignee:login'] === MY_USER_NAME) {
          context.adaptableApi.gridApi.setCellValue({
            columnId: 'state',
            newValue: 'closed',
            primaryKeyValue: data.id,
          });
        }
      });
    },
  };
}

const gridOptions: GridOptions = {
  sideBar: true,
  columnDefs,
  rowHeight: 45,
  enableRangeSelection: true,
  singleClickEdit: true,
  suppressMenuHide: true,
  suppressAggFuncInHeader: true,
  statusBar: {
    statusPanels: [
      { statusPanel: 'agTotalRowCountComponent', align: 'left' },
      { statusPanel: 'agFilteredRowCountComponent' },
      {
        key: 'Center Panel',
        statusPanel: 'AdaptableStatusPanel',
        align: 'center',
      },
    ],
  },
  defaultColDef: {
    enableRowGroup: true,
    enablePivot: true,
    sortable: true,
    filter: true,
    editable: false,
    resizable: true,
    floatingFilter: true,
  },
  enableCharts: true,
  chartThemes: ['ag-vivid'],
};

const adaptableOptions: AdaptableOptions = {
  primaryKey: 'id',
  licenseKey: process.env.REACT_APP_ADAPTABLE_LICENSE_KEY,
  userName: MY_USER_NAME,
  adaptableId: 'Github Support Issue Demo',

  alertOptions: {
    actionHandlers: [
      {
        name: 'set-in-progress',
        handler: (
          button: AlertButton<AlertFormContext>,
          context: AlertFormContext
        ) => {
          let row = (context.alert as AdaptableCellChangedAlert)
            .cellDataChangedInfo?.rowData;

          row = {
            ...row,
            state: 'in-progress',
          };

          context.adaptableApi.gridApi.updateGridData([row]);
        },
      },
    ],
  },
  dataSetOptions: {
    dataSets: [
      {
        name: 'VS Code',
        description: 'microsoft/vscode',
      },
      {
        name: 'Ag Grid',
        description: 'ag-grid/ag-grid',
      },
      {
        name: 'React',
        description: 'facebook/react',
      },
      {
        name: 'Angular',
        description: 'angular/angular',
      },
    ],
  },
  editOptions: {
    isCellEditable: ({ gridCell }) => {
      const rowNode = gridCell.rowNode;
      if (!gridCell || !rowNode || !rowNode.data) {
        return false;
      }
      const rowData = rowNode.data;
      const isAssigneeColumn = gridCell.column.columnId === 'assignee:login';
      const isReactions = gridCell.column.columnId === 'reactions';
      /**
       * Can edit anything if the Issue is his own or is assigned to himself.
       * If the Issue is not his own he can edit only the assignee.
       */
      return isAssigneeColumn || isReactions || isMyIssue(rowData);
    },
  },
  entitlementOptions: {
    moduleEntitlements: [
      {
        adaptableModule: 'Theme',
        accessLevel: 'Hidden',
      },
      {
        adaptableModule: 'CellSummary',
        accessLevel: 'Hidden',
      },
      {
        adaptableModule: 'Shortcut',
        accessLevel: 'Hidden',
      },
      {
        adaptableModule: 'GridFilter',
        accessLevel: 'ReadOnly',
      },
    ],
  },

  dashboardOptions: {
    showQuickSearchInHeader: false,
    customDashboardButtons: [
      {
        onClick: (_, context) => {
          context.adaptableApi.settingsPanelApi.openCustomSettingsPanel(
            'About Issue Demo'
          );
        },
        icon: {
          src: 'info-icon.png',
        },
      },
    ],
    customToolbars: [
      {
        name: 'Issues Management',
        title: 'Issues Management',
        showConfigureButton: false,
        showCloseButton: true,
        toolbarButtons: [
          getCloseAllMyIssuesButton<CustomToolbarButtonContext>(),
          getMyAllIssuesButton<CustomToolbarButtonContext>(),
        ],
      },
    ],
  },
  columnFilterOptions: {
    quickFilterOptions: {
      hideQuickFilterDropdown: ({ column }) => {
        return column.dataType === 'Boolean';
      },
      quickFilterTrigger: 'click',
    },
    defaultStringColumnFilter: (column) => {
      if (column.columnId === 'state') {
        return 'StartsWith';
      }
      return 'Is';
    },
  },
  menuOptions: {
    showAdaptableColumnMenu: (menu, context) => {
      return context?.adaptableColumn?.columnId !== 'actions';
    },
    columnMenuItems: [
      {
        label: (context) => {
          return getIssuesFilterLabel(context.adaptableApi);
        },
        hidden: (context) => {
          return (
            context.isRowGroupColumn ||
            context.adaptableColumn?.columnId !== 'assignee:login'
          );
        },
        onClick: (context) => {
          filterMyIssues(context.adaptableApi);
        },
      },
    ],
    contextMenuItems: [
      {
        label: 'Assign to me',
        hidden: (context) => isMyIssue(context.rowNode.data),
        onClick: (context) => {
          assignToMe(context.adaptableApi, context.rowNode.data);
        },
      },
      {
        label: (context) => {
          return context.rowNode.data.state === 'closed'
            ? 'Open Issue'
            : 'Close Issue';
        },
        hidden: (context) => !isMyIssue(context.rowNode.data),
        onClick: (context) => {
          const isOpen = context.rowNode.data.state !== 'closed';

          if (isOpen) {
            context.adaptableApi.gridApi.setCellValue({
              columnId: 'state',
              newValue: 'closed',
              primaryKeyValue: context.primaryKeyValue,
            });
          } else {
            context.adaptableApi.gridApi.setCellValue({
              columnId: 'state',
              newValue: 'open',
              primaryKeyValue: context.primaryKeyValue,
            });
          }
          //   context.adaptableApi.gridApi.refreshRow(context.primaryKeyValue);
        },
      },
    ],
  },
  notificationsOptions: {
    duration: 10000,
    showProgressBar: true,
    closeWhenClicked: true,
    pauseWhenHovering: true,
  },
  predicateOptions: {
    customPredicateDefs: [
      {
        id: 'live',
        label: 'Live',
        columnScope: {
          ColumnIds: ['state'],
        },
        moduleScope: ['alert', 'columnFilter', 'formatColumn'],
        handler(params: PredicateDefHandlerContext) {
          return ['open', 'in-progress'].includes(params.node.data.state);
        },
      },
      {
        id: 'my issues',
        label: 'My Issues',
        columnScope: {
          ColumnIds: ['user:login'],
        },
        moduleScope: ['alert', 'columnFilter', 'formatColumn'],
        handler(params: PredicateDefHandlerContext) {
          return params.node.data['user:login'] === MY_USER_NAME;
        },
      },
      {
        id: 'my-assignments',
        label: 'My Assignments',
        columnScope: {
          ColumnIds: ['assignee:login'],
        },
        moduleScope: ['alert', 'columnFilter', 'formatColumn'],
        handler(params: PredicateDefHandlerContext) {
          return params.node.data['assignee:login'] === MY_USER_NAME;
        },
      },
    ],
  },
  expressionOptions: {
    customScalarFunctions: {
      GET_MY_USER_NAME: {
        handler: () => {
          return MY_USER_NAME;
        },
        description: 'Retrieves logged in user name',
        signatures: ['  '],
        examples: ['GET_MY_USER_NAME()'],
      },
    },
  },

  toolPanelOptions: {
    customToolPanels: [
      {
        name: 'My Issues',
        title: 'My Issues',
        buttons: [
          getMyAllIssuesButton<CustomToolPanelButtonContext>(),
          getCloseAllMyIssuesButton<CustomToolPanelButtonContext>(),
        ],
      },
    ],
  },
  userInterfaceOptions: {
    alternativeModuleNames: [
      {
        adaptableModule: 'DataSet',
        newName: 'GitHub Repo Selector',
      },
      {
        adaptableModule: 'SystemStatus',
        newName: 'Overview',
      },
    ],
    //  styleClassNames: ['Dashed-Border'],
    editableCellStyle: {
      ClassName: 'Dashed-Border',
    },
    applicationIcon: {
      src: 'https://www.pngkey.com/png/full/178-1787243_github-icon-png-github-icon-white-png.png',
      style: {
        width: 24,
        height: 24,
      },
    },
    editLookUpItems: [
      {
        scope: {
          ColumnIds: ['state'],
        },
        values: (params: EditLookUpContext) => {
          if (params.gridCell?.rawValue === 'closed') {
            return ['open', 'invalid', 'closed'];
          } else {
            return [
              'open',
              'in-progress',
              'blocked',
              'under-review',
              'invalid',
              'closed',
            ];
          }
        },
      },
      {
        scope: {
          ColumnIds: ['assignee:login'],
        },
        values: () => {
          const users = getAllUsers().map((user) => user.login);
          console.log({ users });

          return users;
        },
      },
    ],
  },
  actionColumnOptions: {
    actionColumns: [
      {
        friendlyName: ' ',
        columnId: 'actions',
        actionColumnButton: {
          label: (button, context) => {
            if (!isMyIssue(context.rowNode.data)) {
              return 'Assign Me';
            }

            return context.rowNode.data.state === 'closed' ? 'open' : 'close';
          },
          onClick: (button, context) => {
            const isOpen = context.rowNode.data.state !== 'closed';

            if (!isMyIssue(context.rowNode.data)) {
              assignToMe(context.adaptableApi, context.rowNode.data);
              return;
            }

            if (isOpen) {
              context.adaptableApi.gridApi.setCellValue({
                columnId: 'state',
                newValue: 'closed',
                primaryKeyValue: context.primaryKeyValue,
              });
            } else {
              context.adaptableApi.gridApi.setCellValue({
                columnId: 'state',
                newValue: 'open',
                primaryKeyValue: context.primaryKeyValue,
              });
            }
          },
          buttonStyle: (button, context) => {
            const className = 'issue__action-button';

            if (!isMyIssue(context.rowNode.data)) {
              return {
                tone: 'info',
                variant: 'raised',
                className,
              };
            }

            return {
              tone:
                context.rowNode.data?.state === 'closed' ? 'warning' : 'info',
              variant: 'outlined',
              className,
            };
          },
        },
      },
    ],
  },
  settingsPanelOptions: {
    alwaysShowInDashboard: true,
    alwaysShowInToolPanel: true,
    customSettingsPanels: [
      {
        name: 'About Issue Demo',
        frameworkComponent: AboutPanel,
      },
    ],
    navigation: {
      items: [
        'About Issue Demo',
        '-',
        'Dashboard',
        'ToolPanel',
        'StateManagement',
        '-',
        'Alert',
        'CalculatedColumn',
        'ConditionalStyle',
        'CustomSort',
        'DataSet',
        'Export',
        'Filter',
        'FlashingCell',
        'FormatColumn',
        'FreeTextColumn',
        'Layout',
        'PlusMinus',
        'Query',
        'QuickSearch',
        'Schedule',
        'Shortcut',
        '-',
        'GridInfo',
        'SystemStatus',
        'DataChangeHistory',
        'TeamSharing',
        'Theme',
      ],
    },
  },
  predefinedConfig: {
    Alert: {
      Revision: Date.now(),
      AlertDefinitions: [
        {
          Rule: {
            Predicates: [
              {
                PredicateId: 'my-assignments',
              },
            ],
          },
          Scope: {
            ColumnIds: ['assignee:login'],
          },
          AlertProperties: {
            DisplayNotification: true,
          },
          MessageType: 'Info',
          AlertForm: {
            Buttons: [
              {
                Action: ['set-in-progress', 'highlight-cell'],
                ButtonStyle: {
                  tone: 'neutral',
                  variant: 'outlined',
                },
                Label: 'Set in progress',
              },
              {
                Label: 'OK',
                ButtonStyle: {
                  variant: 'raised',
                },
              },
            ],
          },
        },
      ],
    },
    CalculatedColumn: {
      Revision: Date.now(),
      CalculatedColumns: [
        {
          FriendlyName: 'Days Old',
          ColumnId: 'days_old',
          Query: {
            ScalarExpression: 'DIFF_DAYS(TODAY(), Date([created_at])) ',
          },
          CalculatedColumnSettings: {
            DataType: 'Number',
            Width: 120,
            Filterable: true,
            Sortable: true,
          },
        },
      ],
    },
    CustomSort: {
      CustomSorts: [
        {
          ColumnId: 'assignee:login',
          SortedValues: [MY_USER_NAME],
        },
      ],
    },
    Dashboard: {
      Revision: Date.now(),
      IsInline: true,
      ModuleButtons: ['SettingsPanel'],
      Tabs: [
        {
          Name: 'Main',
          Toolbars: ['Layout', 'Alert', 'DataSet', 'Export', 'SystemStatus'],
        },
        {
          Name: 'Edit',
          Toolbars: ['Issues Management', 'DataChangeHistory', 'BulkUpdate'],
        },
        {
          Name: 'Search',
          Toolbars: ['Query', 'QuickSearch'],
        },
      ],
    },
    StatusBar: {
      StatusBars: [
        {
          Key: 'Center Panel',
          StatusBarPanels: ['Layout'],
        },
      ],
    },
    Export: {
      Reports: [
        {
          Name: 'My Assigned Issues',
          ReportColumnScope: 'ScopeColumns',
          ReportRowScope: 'ExpressionRows',
          Query: {
            BooleanExpression: 'QUERY("My Assigned Issues") ',
          },
          Scope: {
            ColumnIds: ['id', 'title', 'user:login', 'state', 'comments'],
          },
        },
        {
          Name: 'Issues Created by Me',
          ReportColumnScope: 'ScopeColumns',
          ReportRowScope: 'ExpressionRows',
          Query: {
            BooleanExpression: 'QUERY("Created by me") ',
          },
          Scope: {
            ColumnIds: ['id', 'title', 'user:login', 'state', 'assignee:login'],
          },
        },
        {
          Name: 'My Issues Assigned Today',
          ReportColumnScope: 'ScopeColumns',
          ReportRowScope: 'ExpressionRows',
          Query: {
            BooleanExpression:
              'QUERY("Created Today") AND QUERY("My Assigned Issues") ',
          },
          Scope: {
            ColumnIds: ['id', 'title', 'state', 'comments'],
          },
        },
      ],
    },
    FormatColumn: {
      Revision: Date.now(),
      FormatColumns: [
        {
          Scope: {
            DataTypes: ['Date'],
          },
          CellAlignment: 'Center',
          DisplayFormat: {
            Formatter: 'DateFormatter',
            Options: {
              Pattern: 'dd-MM-yyyy',
            },
          },
        },
        {
          Scope: {
            ColumnIds: ['comments', 'days_old'],
          },
          CellAlignment: 'Right',
        },
        {
          Scope: {
            ColumnIds: ['locked'],
          },
          // ColumnStyle: {
          //   CheckBoxStyle: true,
          // },
        },

        // format columns with style
        {
          Style: {
            BackColor: '#f2f2f2',
            BorderColor: '#d3d3d3',
          },
          IncludeGroupedRows: false,
          Scope: {
            All: true,
          },
          Rule: {
            BooleanExpression: '[state] = "closed"',
          },
        },
        {
          Style: {
            BackColor: '#e4ecff',
          },
          IncludeGroupedRows: false,
          Scope: {
            All: true,
          },
          Rule: {
            BooleanExpression: 'QUERY("My Assigned Issues")',
          },
        },
        {
          Style: {
            BackColor: '#ffffe0',
          },
          Scope: {
            All: true,
          },
          Rule: {
            BooleanExpression:
              '[state] != "closed" AND DIFF_DAYS(TODAY(), Date([created_at])) = 1',
          },
          Source: 'User',
        },
        {
          Style: {
            BackColor: '#ffebeb',
          },
          Scope: {
            All: true,
          },
          Rule: {
            BooleanExpression:
              '[state] != "closed" AND DIFF_DAYS(TODAY(), Date([created_at])) > 1',
          },
          Source: 'User',
        },
      ],
    },
    FreeTextColumn: {
      FreeTextColumns: [
        {
          ColumnId: 'observation',
          FriendlyName: 'Observation',
          FreeTextColumnSettings: {
            DataType: 'String',
          },
          DefaultValue: '',
        },
      ],
    },
    Layout: {
      Revision: Date.now(),
      CurrentLayout: 'Grid View',
      Layouts: [
        {
          Name: 'Grid View',
          IsReadOnly: true,
          Columns: [
            'id',
            'title',
            'user:login',
            'assignee:login',
            'state',
            'locked',
            'observation',
            'comments',
            'created_at',
            'updated_at',
            //  'closed_at',
            'days_old',
            'labels',
            'open',
            'close',
            'actions',
            'milestone:title',
            'reactions',
          ],
          ColumnWidthMap: {
            actions: 115,
          },
          PinnedColumnsMap: {
            id: 'left',
            title: 'left',
            open: 'right',
            close: 'right',
            actions: 'right',
          },
          ColumnSorts: [{ ColumnId: 'user:login', SortOrder: 'Desc' }],
        },
        {
          Name: 'Grouped by User',
          Columns: [
            'user:login',
            'title',
            //   'labels',
            'comments',
            'created_at',
            'updated_at',
            //  'closed_at',
            'state',
            'locked',
            'milestone:title',
            'days_old',
            'actions',
            'observation',
            'number',
            'id',
            'milestone',
            'body',
          ],
          ColumnSorts: [{ ColumnId: 'user:login', SortOrder: 'Desc' }],
          RowGroupedColumns: ['user:login'],
          AggregationColumns: {
            comments: 'sum',
          },
        },
        {
          Name: 'Grouped by State',
          Columns: [
            'assignee:login',
            'user:login',
            'title',
            'labels',
            'created_at',
            'updated_at',
            //  'closed_at',
            'locked',
            'milestone:title',
            'days_old',
            'actions',
            'observation',
            'number',
            'id',
            'milestone',
            'body',
            'comments',
          ],
          RowGroupedColumns: ['state'],
        },
      ],
    },
    PlusMinus: {
      Revision: Date.now(),
      PlusMinusNudges: [
        {
          Scope: {
            ColumnIds: ['reactions'],
          },
          NudgeValue: 1,
        },
      ],
    },
    NamedQuery: {
      Revision: Date.now(),
      NamedQueries: [
        {
          Name: 'My Assigned Issues',
          BooleanExpression: 'GET_MY_USER_NAME() = [assignee:login]',
        },
        {
          Name: 'Created By Me',
          BooleanExpression: 'GET_MY_USER_NAME() = [user:login]',
        },
        {
          Name: 'Created Today',
          BooleanExpression: 'DIFF_DAYS(TODAY(), DATE([created_at])) = 0',
        },
      ],
    },
    StyledColumn: {
      Revision: Date.now(),
      StyledColumns: [
        {
          ColumnId: 'state',
          BadgeStyle: {
            Badges: [
              {
                Predicate: {
                  PredicateId: 'Is',
                  Inputs: ['Open'],
                },
                Style: {
                  BackColor: 'DarkBlue',
                  ForeColor: 'white',
                  BorderRadius: 6,
                },
                Icon: {
                  name: 'folder-open',
                },
              },
              {
                Predicate: {
                  PredicateId: 'Is',
                  Inputs: ['Closed'],
                },
                Style: {
                  BackColor: 'Brown',
                  ForeColor: 'white',
                  BorderRadius: 6,
                },
                Icon: {
                  name: 'folder',
                },
              },
            ],
          },
        },
      ],
    },

    ToolPanel: {
      ToolPanels: [
        {
          Name: 'Alert',
        },
        {
          Name: 'BulkUpdate',
        },
        {
          Name: 'CellSummary',
        },
        {
          Name: 'Filter',
        },
        {
          Name: 'Dashboard',
        },
        {
          Name: 'Export',
        },
        {
          Name: 'Layout',
        },
        {
          Name: 'Query',
        },
        {
          Name: 'QuickSearch',
        },
        {
          Name: 'SmartEdit',
        },
        {
          Name: 'SystemStatus',
        },
        {
          Name: 'DataChangeHistory',
        },
        {
          Name: 'My Issues',
          VisibilityMode: 'expanded',
        },
      ],
    },
  },
};

const App: React.FunctionComponent = () => {
  const adaptableApiRef = React.useRef<AdaptableApi>();
  const [repo, setRepo] = React.useState<string>('');

  const issues = useData(adaptableApiRef, repo);

  React.useEffect(() => {
    adaptableApiRef.current?.gridApi.setInitialGridData(issues);
  }, [issues]);

  const handleNewIssue = (event: GridDataChangedInfo) => {
    if (
      Math.random() > 0.5 ||
      event.dataRows[0]?.['assignee:login'] === MY_USER_NAME
    ) {
      return;
    }

    let row = event.dataRows[0];
    assignToMe(event.adaptableApi, row);
  };

  const setInitialDataSet = () => {
    adaptableApiRef.current?.dataSetApi.setDataSet('VS Code');
    setRepo('microsoft/vscode');
    addSystemMessage();
  };

  const handleCellChange = (event: CellChangedInfo) => {
    addSystemMessage();
    if (
      event &&
      event.cellChange &&
      event.cellChange.rowData &&
      event.cellChange.rowData.state &&
      event.cellChange.rowNode
    ) {
      event.adaptableApi.gridApi.refreshRowNode(
        event.cellChange.primaryKeyValue
      );
    }
  };

  const handleDataSetChanged = (event: DataSetSelectedInfo) => {
    setRepo(event.dataSet.description);
    addSystemMessage();
  };

  const addSystemMessage = () => {
    const allMyOpenIssues = getAllMyOpenIssues(adaptableApiRef.current!);
    const numberOfIssues = allMyOpenIssues.length;
    const message =
      numberOfIssues === 0 ? 'No Open Issues' : `${numberOfIssues} Issues`;
    if (numberOfIssues === 0) {
      adaptableApiRef.current?.systemStatusApi.setSuccessSystemStatus(message);
    } else if (numberOfIssues < 3) {
      adaptableApiRef.current?.systemStatusApi.setInfoSystemStatus(message);
    } else if (numberOfIssues < 5) {
      adaptableApiRef.current?.systemStatusApi.setWarningSystemStatus(message);
    } else {
      adaptableApiRef.current?.systemStatusApi.setErrorSystemStatus(message);
    }
  };

  return (
    <div
      className="CRM-GRID"
      style={{ display: 'flex', flexFlow: 'column', height: '100vh' }}
    >
      <AdaptableReact
        style={{ flex: 'none' }}
        gridOptions={gridOptions}
        adaptableOptions={adaptableOptions}
        onAdaptableReady={({ adaptableApi }) => {
          adaptableApiRef.current = adaptableApi;
          adaptableApiGlobal = adaptableApi;
          setInitialDataSet();
          adaptableApi.eventApi.on('CellChanged', handleCellChange);
          adaptableApi.eventApi.on('DataSetSelected', handleDataSetChanged);
          adaptableApi.eventApi.on('GridDataChanged', (event) => {
            addSystemMessage();
            if (event.rowTrigger === 'Add') {
              handleNewIssue(event);
            }
          });
          setTimeout(() => {
            adaptableApi.settingsPanelApi.openCustomSettingsPanel(
              'About Issue Demo'
            );
          }, 2000);
        }}
      />
      <div className="ag-theme-alpine" style={{ flex: 1 }}>
        <AgGridReact gridOptions={gridOptions} modules={modules} />
      </div>
    </div>
  );
};

export default App;
